import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {ApiDataSource} from "../../utils/api-data-source";
import {SafetyObservation} from "../../models/safety-observation.model";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {tap} from "rxjs/operators";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {AssetsSelectorComponent} from "../../shared/assets-selector/assets-selector.component";
import {AssetsService} from "../../assets/assets.service";

@Component({
  selector: 'app-safety-observations-view-assets',
  templateUrl: './safety-observations-view-assets.component.html',
  styleUrls: ['./safety-observations-view-assets.component.scss']
})
export class SafetyObservationsViewAssetsComponent implements OnInit, AfterViewInit {

  @Input('safety_observation_id') safety_observation_id: number;
  @Input('safety_observation') safety_observation: SafetyObservation;

  dataSource: SafetyObservationsAssetsDataSource = new SafetyObservationsAssetsDataSource(this.app, this.api);

  listColumns: string[] = [
    'select',
    'id',
    'name',
    'code',
    'asset_type',
    'flags',
    'status',
    'owner_type',
    'condition_rating',
    'actions'
  ];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public assetsService: AssetsService
  ) { }

  ngOnInit() {
    this.dataSource.safety_observation_id = this.safety_observation_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Display a component dialog for managing assets.
   *
   * @return {void}
   */
  onManageAssets() {
    this.utils.showComponentDialog(
      AssetsSelectorComponent,
      {
        selected: this.dataSource.allIds,
        site_id: this.safety_observation.site_id,
        multiple: true
      },
      {
        width: '1700px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          // Set the newly selected ids.
          this.dataSource.allIds = results;

          // Send API request to sync assets.
          this.api.makeRequest('put', `v2/safety-observations/${this.safety_observation_id}/assets`, {
            asset_ids: this.dataSource.allIds
          })
            .then(() => {
              // Refresh the list.
              this.dataSource.getData();
            });
        }
      }
    );
  }

  /**
   * Unlinks the selected asset.
   *
   * @param {number} id - The ID of the asset to be unlinked.
   * @return {void}
   */
  onUnlinkAsset(id: number) {
    this.utils.showModal(
      'Unlink Selected Asset',
      'Are you sure you want to unlink the selected Asset?',
      () => {
      this.unlink(id, 'Asset was unlinked');
    });
  }

  /**
   * Unlinks the selected assets.
   *
   * @return {void}
   */
  onUnlinkSelected() {
    this.utils.showModal(
      'Unlink Selected Assets',
      'Are you sure you want to unlink the selected Assets?',
      () => {
        this.unlink(this.dataSource.selection.selected.join(','), 'Assets were unlinked');
      });
  }

  /**
   * Unlinks assets with the specified IDs.
   *
   * @param {any} ids - The asset IDs to be unlinked.
   * @param msg
   * @return {void}
   */
  private unlink(ids: any, msg: string) {
    this.api.makeRequest('delete', `v2/safety-observations/${this.safety_observation_id}/assets`, {},
      {
        asset_ids: ids
      })
      .then(() => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class SafetyObservationsAssetsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  safety_observation_id: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/safety-observations/${this.safety_observation_id}/assets`, resetOffset, {});
  }
}
